/**
 * MatchupContent - Main content area with view toggle and player cards
 *
 * Features:
 * - Toggle between "Players" and "Top Bets" views
 * - Team filter tabs (All / Away Team / Home Team)
 * - Player cards with expandable stats
 */
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  Divider,
  useMediaQuery,
  useTheme,
} from 'omaha-design-system';
import { DisplayGame } from '../../data/types';
import PlayerCard from './PlayerCard';
import ProjectionUpdateBanner from './ProjectionUpdateBanner';
import TopBetsTable from './TopBetsTable';

interface MatchupContentProps {
  game: DisplayGame;
}

type ViewMode = 'players' | 'topBets';
type TeamFilter = 'all' | 'away' | 'home';

const MatchupContent: React.FC<MatchupContentProps> = ({ game }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [viewMode, setViewMode] = useState<ViewMode>('players');
  const [teamFilter, setTeamFilter] = useState<TeamFilter>('all');
  const [expandedPlayerId, setExpandedPlayerId] = useState<string | null>(null);

  // Reset filters when game changes
  useEffect(() => {
    setTeamFilter('all');
    setExpandedPlayerId(null);
  }, [game.id]);

  const handleViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: ViewMode | null
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handleTeamFilterChange = (_event: React.SyntheticEvent, newValue: TeamFilter) => {
    setTeamFilter(newValue);
  };

  const handleToggleExpand = useCallback((playerId: string) => {
    setExpandedPlayerId((prev) => (prev === playerId ? null : playerId));
  }, []);

  // Filter players by team
  const filteredPlayers = useMemo(() => {
    if (teamFilter === 'all') {
      return game.players;
    }
    const targetTeam = teamFilter === 'away' ? game.awayTeam.abbr : game.homeTeam.abbr;
    return game.players.filter(
      (player) => player.team.toUpperCase() === targetTeam.toUpperCase()
    );
  }, [game.players, teamFilter, game.awayTeam.abbr, game.homeTeam.abbr]);

  // Get best EV for a player (highest EV across all their props)
  const getPlayerBestEV = (player: typeof game.players[0]): number => {
    if (!player.props || player.props.length === 0) return -Infinity;
    return Math.max(
      ...player.props.map((prop) =>
        Math.max(...(prop.sportsbooks?.map((sb) => sb.evPct ?? -Infinity) || [-Infinity]))
      )
    );
  };

  // Sort players by best EV (highest first)
  const sortedPlayers = useMemo(() => {
    return [...filteredPlayers].sort((a, b) => getPlayerBestEV(b) - getPlayerBestEV(a));
  }, [filteredPlayers]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflowY: 'auto' }}>
      {/* Projection Update Banner */}
      <ProjectionUpdateBanner gamePlayerIds={game.players.map((p) => p.id)} />

      {/* View Toggle and Team Filter */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          px: isMobile ? 2 : 0,
        }}
      >
        {/* View Mode Toggle */}
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size={isMobile ? 'small' : 'medium'}
          sx={{
            borderRadius: '8px',
            '& .MuiToggleButtonGroup-grouped': {
              borderColor: 'rgba(255, 255, 255, 0.12)',
            },
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              px: isMobile ? 1 : 2,
              py: isMobile ? 0.75 : 1,
              fontSize: isMobile ? '13.33px' : '14px',
              fontWeight: 400,
              color: 'text.primary',
              borderColor: 'rgba(255, 255, 255, 0.12)',
              '&.Mui-selected': {
                backgroundColor: '#42A5F5',
                color: 'rgba(0, 0, 0, 0.87)',
                '&:hover': {
                  backgroundColor: '#1E88E5',
                },
              },
            },
          }}
        >
          <ToggleButton value="players">Players</ToggleButton>
          <ToggleButton value="topBets">Top Bets</ToggleButton>
        </ToggleButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />

        {/* Team Filter */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            minWidth: 0,
            flex: isMobile ? 1 : 'none',
          }}
        >
          <Typography
            color="text.disabled"
            sx={{ fontSize: '13.33px', lineHeight: 1.5, flexShrink: 0 }}
          >
            Team:
          </Typography>
          <Tabs
            value={teamFilter}
            onChange={handleTeamFilterChange}
            variant="standard"
            scrollButtons={false}
            sx={{
              minHeight: 'auto',
              minWidth: 0,
              '& .MuiTabs-indicator': {
                display: 'none',
              },
              '& .MuiTabs-flexContainer': {
                flexWrap: 'nowrap',
              },
              '& .MuiTab-root': {
                minWidth: 'auto',
                minHeight: 'auto',
                padding: '6px 8px',
                textTransform: 'none',
                fontSize: '13.33px',
                fontWeight: 400,
                color: 'text.secondary',
                whiteSpace: 'nowrap',
                '&.Mui-selected': {
                  fontWeight: 600,
                  color: 'text.primary',
                },
              },
            }}
          >
            <Tab label="All" value="all" />
            <Tab label={isMobile ? game.awayTeam.abbr : game.awayTeam.name} value="away" />
            <Tab label={isMobile ? game.homeTeam.abbr : game.homeTeam.name} value="home" />
          </Tabs>
        </Box>
      </Box>

      {/* Content based on view mode */}
      {viewMode === 'players' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: isMobile ? 2 : 0 }}>
          {sortedPlayers.length > 0 ? (
            sortedPlayers.map((player) => (
              <PlayerCard
                key={player.id}
                player={player}
                game={game}
                isExpanded={expandedPlayerId === player.id}
                onToggleExpand={handleToggleExpand}
                isMobile={isMobile}
              />
            ))
          ) : (
            <Box
              sx={{
                textAlign: 'center',
                py: 4,
                color: 'text.secondary',
              }}
            >
              <Typography>No players found for this filter.</Typography>
            </Box>
          )}
        </Box>
      ) : (
        // Top Bets View - Filtered props table for this game
        <TopBetsTable game={game} teamFilter={teamFilter} />
      )}
    </Box>
  );
};

export default MatchupContent;
